/**
 * Správa přihlášení na klientovi: expirace tokenu, tichá obnova
 * a reakce na vypršení uprostřed práce.
 *
 * Token platí 24 h. Brigádnice hlásí celý den, takže token vyprší
 * uprostřed směny — dřív web ukázal syrové „Forbidden JWT". Teď se
 * hodinu před vypršením tiše obnoví a když přesto vyprší, uživatel
 * dostane srozumitelnou hlášku a po přihlášení se vrátí tam, kde byl.
 */

export const TOKEN_STORAGE_KEY = 'token';

/** Kolik času před vypršením se token tiše obnovuje (ms). */
export const REFRESH_BEFORE_EXPIRY_MS = 60 * 60 * 1000;

/** Hláška po vypršení přihlášení. */
export const SESSION_EXPIRED_MESSAGE = 'Přihlášení vypršelo, přihlaste se znovu.';

/**
 * Přečte expiraci z JWT bez ověření podpisu (podpis ověřuje server).
 * @param {string|null|undefined} token
 * @returns {number|null} Čas vypršení v ms, nebo null když nejde přečíst
 */
export function tokenExpiresAt(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (error) {
    return null;
  }
}

/**
 * Má se token teď tiše obnovit? Ano, když platí a zbývá mu méně než hodina.
 * @param {string|null} token
 * @param {number} [now]
 * @returns {boolean}
 */
export function shouldRefreshToken(token, now = Date.now()) {
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt === null) return false;
  if (expiresAt <= now) return false;
  return expiresAt - now < REFRESH_BEFORE_EXPIRY_MS;
}

/**
 * Je odpověď serveru známka vypršelého či neplatného přihlášení?
 * Server vrací 403 „Forbidden JWT" (vypršel/neplatný) a 401 „JWT problem"
 * (chybí) nebo „User no longer exists".
 * @param {object} error - Chyba z axiosu
 * @returns {boolean}
 */
export function isAuthFailure(error) {
  const status = error && error.response && error.response.status;
  if (status !== 401 && status !== 403) return false;
  const message = String((error.response.data && error.response.data.message) || '');
  return /jwt|token|no longer exists/i.test(message);
}

let expiredHandler = null;
let handling = false;

/**
 * Zaregistruje reakci na vypršení přihlášení (nastavuje main.js — modul
 * api.js nemůže importovat router, vznikl by cyklus).
 * @param {(details: {message: string}) => void} handler
 */
export function onSessionExpired(handler) {
  expiredHandler = handler;
}

/**
 * Vyvolá reakci na vypršení; víc souběžných 403 spustí handler jen jednou.
 */
export function notifySessionExpired() {
  if (handling || !expiredHandler) return;
  handling = true;
  try {
    expiredHandler({ message: SESSION_EXPIRED_MESSAGE });
  } finally {
    // Další vypršení (po novém přihlášení) se má zase zpracovat
    setTimeout(() => { handling = false; }, 0);
  }
}

/** Jen pro testy — vrátí modul do výchozího stavu. */
export function _resetSessionForTests() {
  expiredHandler = null;
  handling = false;
}
